// src/components/ChooseAssessmentTypeModal/ChooseAssessmentTypeModal.tsx
import React, { useContext, useState } from 'react';
import {
  HandShakeIcons,
  UserCheckIcons,
  UsersIcons,
} from '../../../../icons/icons';
import { DialogActionButtons } from '../../../../ui/components/Common/Dialog/DialogActionButtons';
import { AssessmentContext } from '../../../../utils/contexts/contexts';
import {
  FeatureNames,
  Features,
} from '../../../../utils/helpers/subscriptionEnum';
import PermissionGate from '../../../../utils/hooks/PermissionGate';

import { ASSESSMENT_TYPE } from '../Constants/AssessmentTypes';
export type AssessmentType =
  | typeof ASSESSMENT_TYPE[keyof typeof ASSESSMENT_TYPE]
  | null;
interface ChooseAssessmentTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccessClose: () => void;
}

const assessmentOptions: {
  value: AssessmentType;
  label: string;
  icon: React.ReactNode;
  featureName: FeatureNames;
  requiredFeature: Features;
}[] = [
  {
    value: ASSESSMENT_TYPE.CANDIDATES,
    label: 'Job Candidates',
    icon: <UserCheckIcons className='w-10 h-10' />,
    featureName: FeatureNames.CANDIDATE,
    requiredFeature: Features.CANDIDATE,
  },
  {
    value: ASSESSMENT_TYPE.TEAM_MEMBERS,
    label: 'Team Members',
    icon: <UsersIcons className='w-10 h-10' />,
    featureName: FeatureNames.EMPLOYEE,
    requiredFeature: Features.EMPLOYEE,
  },
  {
    value: ASSESSMENT_TYPE.PARTNERS,
    label: 'Partners',
    icon: <HandShakeIcons className='w-10 h-10' />,
    featureName: FeatureNames.PARTNER,
    requiredFeature: Features.PARTNER,
  },
];

export const ChooseAssessmentTypeModal: React.FC<ChooseAssessmentTypeModalProps> =
  ({ open, onClose, onSuccessClose }) => {
    const { assessmentState, setAssessmentState } =
      useContext(AssessmentContext);
    const [selectedType, setSelectedType] =
      useState<AssessmentType | null>(null);

    const applySelection = () => {
      if (!selectedType) return;
      setAssessmentState({
        ...assessmentState,
        assessmentType: selectedType,
      });
      onSuccessClose();
    };

    return (
      <>
        { open && (
          <div className='fixed inset-0 z-50 overflow-y-auto'>
            { /* Modern Backdrop with Blur Effect */ }
            <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
              <div
                  className='fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity'
                  onClick={onClose}
              />

              { /* Modern Modal Container */ }
              <div className='inline-block align-bottom bg-white rounded-3xl px-8 py-6 text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full border border-gray-100'>
                { /* Modern Header with Icon */ }
                <div className='text-center mb-8'>
                  <div className='mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 mb-4'>
                    <svg
                        className='h-8 w-8 text-white'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth='2'
                          d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                      />
                    </svg>
                  </div>
                  <h2 className='text-2xl font-bold text-gray-900 mb-2'>
                    Choose Assessment Type
                  </h2>
                  <p className='text-gray-600 text-sm'>
                    Select the type of assessment you&apos;d like to create for
                    your organization
                  </p>
                </div>

                { /* Modern Card Grid */ }
                <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
                  { assessmentOptions.map(
                    ({ value, label, icon, requiredFeature }) => (
                      <PermissionGate
                          key={value ?? label}
                          requiredPermissions={[]}
                          requiredFeature={requiredFeature}
                      >
                        <button
                            type='button'
                            className={`
                            group relative p-6 flex flex-col items-center
                            border-2 rounded-2xl cursor-pointer transition-all duration-300
                            hover:shadow-lg hover:-translate-y-1
                            ${
                              selectedType === value
                                ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-500 shadow-lg scale-105'
                                : 'border-gray-200 hover:border-green-300 hover:shadow-md bg-white'
                            }
                          `}
                            onClick={() => setSelectedType(value)}
                        >
                          { /* Selection Indicator */ }
                          <div
                              className={`
                            absolute -top-2 -right-2 w-6 h-6 rounded-full border-2 
                            flex items-center justify-center transition-all duration-200
                            ${
                              selectedType === value
                                ? 'bg-green-500 border-green-500'
                                : 'bg-white border-gray-300 group-hover:border-green-300'
                            }
                          `}
                          >
                            { selectedType === value && (
                              <svg
                                  className='w-3 h-3 text-white'
                                  fill='currentColor'
                                  viewBox='0 0 20 20'
                              >
                                <path
                                    fillRule='evenodd'
                                    d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                                    clipRule='evenodd'
                                />
                              </svg>
                            ) }
                          </div>

                          { /* Icon with Modern Styling */ }
                          <div
                              className={`
                            mb-4 p-4 rounded-xl transition-all duration-300
                            ${
                              selectedType === value
                                ? 'bg-white shadow-md text-green-600'
                                : 'bg-gray-50 text-gray-600 group-hover:bg-green-50 group-hover:text-green-600'
                            }
                          `}
                          >
                            { icon }
                          </div>

                          { /* Label with Modern Typography */ }
                          <h3
                              className={`
                            text-lg font-semibold text-center transition-colors duration-200
                            ${
                              selectedType === value
                                ? 'text-green-900'
                                : 'text-gray-700 group-hover:text-green-900'
                            }
                          `}
                          >
                            { label }
                          </h3>

                          { /* Subtitle for context */ }
                          <p className='text-xs text-gray-500 mt-2 text-center px-2'>
                            { value === ASSESSMENT_TYPE.CANDIDATES &&
                              'Evaluate potential hires' }
                            { value === ASSESSMENT_TYPE.TEAM_MEMBERS &&
                              'Assess current employees' }
                            { value === ASSESSMENT_TYPE.PARTNERS &&
                              'Evaluate business partners' }
                          </p>
                        </button>
                      </PermissionGate>
                    ),
                  ) }
                </div>

                { /* Modern Action Buttons */ }
                <div className='flex items-center justify-between pt-6 border-t border-gray-100'>
                  <button
                      onClick={onClose}
                      className='px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-all duration-200 hover:shadow-md'
                  >
                    Cancel
                  </button>
                  <button
                      onClick={applySelection}
                      disabled={!selectedType}
                      className={`
                      px-8 py-3 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2
                      ${
                        selectedType
                          ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }
                    `}
                  >
                    <span>{ selectedType ? 'Continue' : 'Select a type' }</span>
                    { selectedType && (
                      <svg
                          className='w-4 h-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                            d='M13 7l5 5m0 0l-5 5m5-5H6'
                        />
                      </svg>
                    ) }
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) }
      </>
    );
  };
